import React, { useState, useEffect } from 'react';
import { BsMicrosoftTeams } from 'react-icons/bs';
import { useAuth } from '../contexts/AuthContext';
import {
  getConnectedProviders,
  connectGoogleCalendar,
  connectMicrosoftCalendar,
  syncCalendarEvents,
  disconnectCalendar,
  CalendarProvider
} from '../services/calendarIntegration';
import '../styles/CalendarSyncModal.css';

interface CalendarSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventsImported: () => void;
}

const CalendarSyncModal: React.FC<CalendarSyncModalProps> = ({ isOpen, onClose, onEventsImported }) => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<CalendarProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadProviders();
    }
  }, [isOpen, user]);

  const loadProviders = async () => {
    if (!user) return;
    try {
      const connectedProviders = await getConnectedProviders(user.id);
      setProviders(connectedProviders);
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    setLoading(true);
    setMessage(null);

    try {
      let result;
      if (provider === 'google') {
        result = await connectGoogleCalendar();
      } else {
        result = await connectMicrosoftCalendar();
      }

      if (result.success && result.redirecting) {
        // Real OAuth - will redirect
        setMessage({
          type: 'info',
          text: `Redirecting to ${provider === 'google' ? 'Google' : 'Microsoft'} OAuth...`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to connect to ${provider === 'google' ? 'Google Calendar' : 'Microsoft Teams'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (provider: 'google' | 'microsoft') => {
    if (!user) return;

    setSyncing(provider);
    setMessage(null);

    try {
      const result = await syncCalendarEvents(user.id, provider);
      setMessage({
        type: 'success',
        text: `Successfully imported ${result.eventsImported} events from ${provider === 'google' ? 'Google Calendar' : 'Microsoft Teams'}`
      });

      // Update provider status
      setProviders(prev => prev.map(p =>
        p.type === provider ? { ...p, connected: true, lastSync: new Date() } : p
      ));

      // Notify parent to refresh events
      setTimeout(() => {
        onEventsImported();
      }, 1000);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Sync error:', error);
      setMessage({
        type: 'error',
        text: `Failed to sync events: ${message}`
      });
    } finally {
      setSyncing(null);
    }
  };

  const handleDisconnect = async (provider: 'google' | 'microsoft') => {
    if (!user) return;

    if (!window.confirm(`Are you sure you want to disconnect ${provider === 'google' ? 'Google Calendar' : 'Microsoft Teams'}?`)) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await disconnectCalendar(user.id, provider);

      setMessage({
        type: 'success',
        text: `Disconnected from ${provider === 'google' ? 'Google Calendar' : 'Microsoft Teams'}`
      });

      // Update provider status
      setProviders(prev => prev.map(p =>
        p.type === provider ? { ...p, connected: false, lastSync: undefined } : p
      ));
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to disconnect from ${provider === 'google' ? 'Google Calendar' : 'Microsoft Teams'}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="calendar-sync-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Calendar Events</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          <p className="modal-description">
            Connect your Google Calendar or Microsoft Teams to automatically import your meetings.
          </p>

          {message && (
            <div className={`sync-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="providers-list">
            {providers.map(provider => (
              <div key={provider.id} className="provider-card">
                <div className="provider-info">
                  <div className="provider-icon">
                    {provider.type === 'google' ? (
                      <svg width="32" height="32" viewBox="0 0 48 48">
                        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
                      </svg>
                    ) : (
                      <BsMicrosoftTeams size={32} color="#6264A7" />
                    )}
                  </div>
                  <div className="provider-details">
                    <h3>{provider.name}</h3>
                    <span className={`status-badge ${provider.connected ? 'connected' : 'disconnected'}`}>
                      {provider.connected ? 'Connected' : 'Not Connected'}
                    </span>
                    {provider.lastSync && (
                      <p className="last-sync">
                        Last synced: {new Date(provider.lastSync).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="provider-actions">
                  {!provider.connected ? (
                    <button
                      className="btn-primary"
                      onClick={() => handleConnect(provider.type)}
                      disabled={loading}
                    >
                      {loading ? 'Connecting...' : 'Connect'}
                    </button>
                  ) : (
                    <div className="connected-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => handleSync(provider.type)}
                        disabled={syncing === provider.type}
                      >
                        {syncing === provider.type ? (
                          <>
                            <span className="spinner"></span>
                            Syncing...
                          </>
                        ) : (
                          'Sync Now'
                        )}
                      </button>
                      <button
                        className="btn-danger-text"
                        onClick={() => handleDisconnect(provider.type)}
                        disabled={loading || syncing === provider.type}
                        style={{ marginLeft: '10px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                      >
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="sync-info">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <p>Events will be imported and automatically synced. You can disconnect at any time.</p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarSyncModal;
